import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "../lib/site";

const EQUIPMENT_LINKS = [
  { to: "/equipment", label: "All Equipment We Buy" },
  { to: "/circuit-breakers", label: "Circuit Breakers" },
  { to: "/bus-plugs-disconnects", label: "Bus Plugs & Disconnects" },
  { to: "/transformers-switchgear", label: "Transformers & Switchgear" },
  { to: "/electrical-wire-cable", label: "Electrical Wire & Cable" },
  { to: "/industrial-surplus", label: "Industrial Surplus" },
  { to: "/warehouse-liquidations", label: "Warehouse Liquidations" },
];

const TOP_LINKS = [
  { to: "/", label: "Home" },
  { to: "/sell-your-surplus", label: "Sell Your Surplus" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

const linkCls = ({ isActive }) =>
  `px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-150 hover:text-ves-green ${
    isActive ? "text-ves-green" : "text-white/85"
  }`;

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();

  const closeAll = () => {
    setMobileOpen(false);
    setDropOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-ves-black/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-20 lg:px-8">
        <Link to="/" onClick={closeAll} aria-label="Velocity Electrical Surplus home">
          <Logo />
        </Link>

        <nav className="hidden items-center lg:flex" aria-label="Main navigation">
          <NavLink to="/" className={linkCls} data-testid="nav-home">
            Home
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setDropOpen(true)}
            onMouseLeave={() => setDropOpen(false)}
          >
            <button
              type="button"
              onClick={() => setDropOpen((v) => !v)}
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold tracking-wide text-white/85 transition-colors duration-150 hover:text-ves-green"
              data-testid="nav-equipment-dropdown"
              aria-expanded={dropOpen}
            >
              Equipment We Buy
              <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${dropOpen ? "rotate-180" : ""}`} />
            </button>
            {dropOpen && (
              <div className="absolute left-0 top-full w-72 border border-white/10 bg-ves-charcoal py-2 hard-shadow-sm">
                {EQUIPMENT_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={closeAll}
                    className="block px-4 py-2.5 text-sm font-medium text-white/85 transition-colors duration-150 hover:bg-ves-forest hover:text-white"
                    data-testid={`nav-drop-${l.to.replace(/\//g, "") || "all"}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {TOP_LINKS.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.to.replace(/\//g, "")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={BUSINESS.phoneTel}
            className="flex items-center gap-2 text-sm font-bold text-white transition-colors duration-150 hover:text-ves-green"
            data-testid="nav-call-link"
          >
            <Phone className="h-4 w-4 text-ves-green" />
            CALL {BUSINESS.phoneDisplay}
          </a>
          <Link
            to="/sell-your-surplus"
            className="bg-ves-green px-5 py-2.5 font-heading text-sm font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
            data-testid="nav-get-offer-btn"
          >
            GET AN OFFER
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={BUSINESS.phoneTel}
            className="flex h-10 w-10 items-center justify-center bg-ves-green text-ves-black"
            aria-label={`Call ${BUSINESS.phoneDisplay}`}
            data-testid="nav-mobile-call"
          >
            <Phone className="h-5 w-5" />
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center border border-white/20 text-white"
            aria-label="Toggle menu"
            data-testid="nav-mobile-menu-button"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          className="border-t border-white/10 bg-ves-black px-4 pb-6 pt-2 lg:hidden"
          aria-label="Mobile navigation"
          data-testid="nav-mobile-menu"
          key={location.pathname}
        >
          {[...TOP_LINKS.slice(0, 1), ...EQUIPMENT_LINKS, ...TOP_LINKS.slice(1)].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={closeAll}
              className={({ isActive }) =>
                `block border-b border-white/5 py-3 text-base font-semibold ${
                  isActive ? "text-ves-green" : "text-white/85"
                }`
              }
              data-testid={`nav-mobile-${l.to.replace(/\//g, "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/sell-your-surplus"
            onClick={closeAll}
            className="mt-4 block bg-ves-green px-5 py-3 text-center font-heading text-base font-black tracking-wide text-ves-black"
            data-testid="nav-mobile-get-offer-btn"
          >
            GET AN OFFER
          </Link>
        </nav>
      )}
    </header>
  );
};
